<script setup lang="ts">
import { useTranslation } from 'i18next-vue';
import { useBrowserDownload } from '@peanuds/composables';
import { useUserAccountsStore } from '@peanuds/shared-state';
import ProgressSpinner from 'primevue/progressspinner';
import Tag from 'primevue/tag';
import { computed, watch } from 'vue';

import {
  getAmountLocale,
  formatDateValue,
  isDateOnlyValue,
  normalizeIban,
  parseDateValue,
} from '@peanuds/shared';
import {
  TransactionIncomingIcon,
  TransactionOutgoingIcon,
  UiButton,
  UiCopyToggleButton,
  UiDialogCloseButton,
  UiDialogShell,
} from '@peanuds/ui';

import { useTransactionDetailsDialogData } from '../internal/useTransactionDetailsDialogData';
import { useTransactionDetailsSelection } from '../internal/useTransactionDetailsSelection';
import type {
  TransactionDetailsDialogData,
  TransactionDetailsDialogDirection,
  TransactionDetailsDialogSource,
  TransactionDetailsDialogStatusSeverity,
} from '../transaction-details.types';

interface Props {
  visible: boolean;
  transactionId?: string | null;
  direction?: TransactionDetailsDialogDirection | null;
  transactionSource?: TransactionDetailsDialogSource | null;
}

interface Emits {
  'update:visible': [value: boolean];
  close: [];
}

type FooterActionSlotProps = {
  dialogData: TransactionDetailsDialogData | null;
  close: () => void;
};

const {
  visible,
  transactionId = null,
  direction = null,
  transactionSource = null,
} = defineProps<Props>();
const emit = defineEmits<Emits>();
defineSlots<{
  'footer-message'?: () => unknown;
  'footer-action'?: (props: FooterActionSlotProps) => unknown;
}>();

const { t } = useTranslation();
const userAccountsStore = useUserAccountsStore();
const { triggerBrowserDownload } = useBrowserDownload();

const { resolvedDirection, resolvedTransactionId, activeSelection } =
  useTransactionDetailsSelection({
    visible: () => visible,
    transactionId: () => transactionId,
    direction: () => direction,
    transactionSource: () => transactionSource,
  });

const {
  dialogData,
  downloadStatement,
  error: detailError,
  isLoading,
  isDownloadingStatement,
} = useTransactionDetailsDialogData({
  selection: activeSelection,
});

const stateMessage = computed(() => {
  if (!resolvedTransactionId.value || !resolvedDirection.value) {
    return t('transactionDetails.states.unavailable');
  }
  if (detailError.value) return t('transactionDetails.states.error');

  return t('transactionDetails.states.empty');
});

const handleClose = () => {
  emit('update:visible', false);
  emit('close');
};

const canDownloadStatement = computed(() => {
  return Boolean(resolvedTransactionId.value && resolvedDirection.value && !isLoading.value);
});

const handleDownloadStatement = async () => {
  const statementFile = await downloadStatement();

  if (!statementFile) return;

  triggerBrowserDownload(
    statementFile.blob,
    friendlyStatementFileName.value || statementFile.fileName,
  );
};

const summaryName = computed(() => {
  return dialogData.value?.summaryName?.trim() || dialogData.value?.beneficiaryName?.trim() || '-';
});

const getNumericValue = (value: number | string | null | undefined): number | null => {
  if (value === undefined || value === null) return null;

  const numericValue = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(numericValue) ? null : numericValue;
};

const formatCurrencyAmount = (
  value: number | string | null | undefined,
  currency: string | null | undefined,
): string => {
  const numericValue = getNumericValue(value);
  if (numericValue === null) return '-';

  const resolvedCurrency = currency || 'USD';
  const absoluteValue = Math.abs(numericValue);

  try {
    return new Intl.NumberFormat(getAmountLocale(), {
      style: 'currency',
      currency: resolvedCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(absoluteValue);
  } catch {
    return absoluteValue.toFixed(2);
  }
};

const formatAmountNoSign = (tx: TransactionDetailsDialogData | null): string => {
  if (!tx) return '-';

  return formatCurrencyAmount(tx.amount, tx.currency);
};

const formatLongDateTime = (tx: TransactionDetailsDialogData | null): string => {
  const candidate = tx?.dateTime;

  if (!candidate) return '-';

  const dateOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  };

  try {
    const datePart = formatDateValue(candidate, dateOptions);

    if (!datePart) return '-';

    if (isDateOnlyValue(candidate)) return datePart;

    const timePart = formatDateValue(candidate, {
      hour: '2-digit',
      minute: '2-digit',
    });

    if (!timePart) return '-';

    return `${datePart} ${t('transactionDetails.fields.at')} ${timePart}`;
  } catch {
    const fallback = parseDateValue(candidate);

    if (!fallback) return '-';

    return isDateOnlyValue(candidate) ? fallback.toLocaleDateString() : fallback.toLocaleString();
  }
};

const formattedDateTime = computed(() => formatLongDateTime(dialogData.value));
const formattedAmount = computed(() => formatAmountNoSign(dialogData.value));

watch(
  () => dialogData.value?.fromAccountId,
  async (fromAccountId) => {
    if (!fromAccountId) return;
    if (userAccountsStore.getAccountById(fromAccountId)) return;

    await userAccountsStore.ensureAccountsLoaded();
  },
  { immediate: true },
);

const truncateIban = (value: string): string => {
  const compact = value.replace(/\s+/g, '');
  if (compact.length <= 24) return compact;

  return `${compact.slice(0, 16)}..${compact.slice(-4)}`;
};

const sourceAccount = computed(() => {
  const fromAccountId = dialogData.value?.fromAccountId?.trim();
  if (!fromAccountId) return undefined;

  return userAccountsStore.getAccountById(fromAccountId);
});

const fromAccountName = computed(() => {
  return (
    dialogData.value?.fromAccountName?.trim() || sourceAccount.value?.friendlyName?.trim() || ''
  );
});

const fromAccountTagLabel = computed(() => {
  const name = fromAccountName.value.trim();
  if (!name) return '';

  return `${dialogData.value?.currency || sourceAccount.value?.currency || 'USD'} ${name}`;
});

const fromAccountIbanRaw = computed(() => {
  const sourceAccountIban =
    typeof sourceAccount.value?.iban === 'string' ? sourceAccount.value.iban.trim() : '';

  return dialogData.value?.fromAccountIban?.trim() || sourceAccountIban || '';
});

const fromAccountIban = computed(() => {
  const raw = fromAccountIbanRaw.value;
  return raw ? truncateIban(raw) : '-';
});

const hasFromAccountDetails = computed(() => {
  return Boolean(fromAccountTagLabel.value || fromAccountIbanRaw.value);
});

const beneficiaryDisplayName = computed(() => {
  return dialogData.value?.beneficiaryName?.trim() || dialogData.value?.summaryName?.trim() || '-';
});

const transactionIdentifier = computed(() => {
  return dialogData.value?.transactionNumber?.trim() || '-';
});

const bankNameAddress = computed(() => {
  return dialogData.value?.bankNameAddress?.trim() || '-';
});

const reference = computed(() => {
  return dialogData.value?.reference?.trim() || '-';
});

const toFileNameSegment = (value: string | null | undefined): string => {
  const normalized = value?.trim();
  if (!normalized || normalized === '-') return '';

  return normalized
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
};

const getStatementDateSegment = (value: string | null | undefined): string => {
  if (!value || value === '-') return '';

  const parsedDate = parseDateValue(value);
  if (!parsedDate) return '';

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const day = String(parsedDate.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const friendlyStatementFileName = computed(() => {
  const direction = resolvedDirection.value;
  const transactionIdSegment = toFileNameSegment(transactionIdentifier.value);

  if (!direction) {
    return transactionIdSegment ? `transaction-confirmation_${transactionIdSegment}.pdf` : '';
  }

  const counterparty =
    direction === 'out'
      ? beneficiaryDisplayName.value
      : summaryName.value || beneficiaryDisplayName.value;
  const counterpartySegment = toFileNameSegment(counterparty);
  const dateSegment = getStatementDateSegment(dialogData.value?.dateTime);
  const prefix = direction === 'out' ? 'payment-confirmation' : 'payment-receipt';
  const segments = [prefix, counterpartySegment, dateSegment].filter(Boolean);

  if (segments.length > 1) return `${segments.join('_')}.pdf`;

  return transactionIdSegment
    ? `transaction-confirmation_${transactionIdSegment}.pdf`
    : `${prefix}.pdf`;
});

const initiatedByName = computed(() => {
  return dialogData.value?.initiatedByName?.trim() || '-';
});

// Figma centres a signed amount under the direction tag.
const signedAmount = computed(() => {
  const formatted = formattedAmount.value;

  if (formatted === '-') return formatted;

  return `${resolvedDirection.value === 'out' ? '-' : '+'} ${formatted}`;
});

const directionTag = computed(() => {
  return resolvedDirection.value === 'out'
    ? { icon: TransactionOutgoingIcon, label: t('transactionDetails.direction.outgoing') }
    : { icon: TransactionIncomingIcon, label: t('transactionDetails.direction.incoming') };
});

const statusTag = computed(() => {
  const statusLabelKey = dialogData.value?.statusLabelKey?.trim();
  const fallbackLabel =
    (statusLabelKey ? t(statusLabelKey) : dialogData.value?.statusLabel?.trim()) ||
    t('transactionDetails.status.pending');
  const fallbackSeverity: TransactionDetailsDialogStatusSeverity =
    dialogData.value?.statusSeverity ?? 'secondary';

  return {
    value: fallbackLabel,
    severity: fallbackSeverity,
  };
});
</script>

<template>
  <UiDialogShell
    :visible="visible"
    width="27.5rem"
    max-width="100vw"
    :dismissable-mask="false"
    :show-pattern="false"
    surface="plain"
    position="right"
    labelled-by="transaction-details-dialog-title"
    :pt="{ mask: { class: 'transaction-details__mask' } }"
    @update:visible="$emit('update:visible', $event)"
  >
    <template #surface>
      <div class="transaction-details dots-pattern-sheet" data-testid="transaction-details-dialog">
        <div class="transaction-details__header">
          <h3 id="transaction-details-dialog-title" class="transaction-details__title">
            {{ t('transactionDetails.title') }}
          </h3>
          <UiDialogCloseButton label="Close" @click="handleClose" />
        </div>

        <div class="transaction-details__body">
          <div
            v-if="isLoading"
            class="transaction-details__state transaction-details__state--loading"
          >
            <ProgressSpinner
              class="transaction-details__spinner"
              strokeWidth="6"
              animationDuration="1s"
              aria-label="Loading transaction details"
            />
            <p class="transaction-details__state-message">
              {{ t('transactionDetails.states.loading') }}
            </p>
          </div>

          <div v-else-if="dialogData" class="transaction-details__details-content">
            <div class="transaction-details__summary">
              <Tag
                class="transaction-details__direction-tag"
                data-testid="transaction-details-direction-tag"
                :value="directionTag.label"
                severity="info"
              >
                <template #icon>
                  <component :is="directionTag.icon" class="transaction-details__direction-icon" />
                </template>
              </Tag>
              <p class="transaction-details__amount">
                {{ signedAmount }}
              </p>
              <p class="transaction-details__date">{{ formattedDateTime }}</p>
            </div>

            <div class="transaction-details__rows">
              <div class="transaction-details__row transaction-details__row--inline">
                <p class="transaction-details__label">
                  {{ t('transactionDetails.fields.status') }}
                </p>
                <Tag v-bind="statusTag" data-testid="transaction-details-status-tag" />
              </div>

              <div v-if="hasFromAccountDetails" class="transaction-details__row">
                <div class="transaction-details__row-main">
                  <p class="transaction-details__label">
                    {{ t('transactionDetails.fields.sentFrom') }}
                  </p>
                  <div class="transaction-details__sent-from">
                    <span
                      v-if="fromAccountTagLabel"
                      class="transaction-details__account-tag"
                      :data-testid="
                        resolvedDirection === 'in' ? 'transaction-details-summary-name' : undefined
                      "
                    >
                      {{ fromAccountTagLabel }}
                    </span>
                    <span class="transaction-details__value-inline">{{ fromAccountIban }}</span>
                  </div>
                </div>
                <UiCopyToggleButton
                  button-size="2.375rem"
                  icon-size="1rem"
                  icon-dark
                  :tooltip-text="t('transactionDetails.fields.sentFrom')"
                  :copy-text="normalizeIban(fromAccountIbanRaw)"
                  :disabled="!fromAccountIbanRaw"
                  class="transaction-details__copy-btn"
                />
              </div>

              <div class="transaction-details__row">
                <div class="transaction-details__row-main">
                  <p class="transaction-details__label">
                    {{ t('transactionDetails.fields.beneficiary') }}
                  </p>
                  <p
                    class="transaction-details__value"
                    :data-testid="
                      resolvedDirection === 'out' ? 'transaction-details-summary-name' : undefined
                    "
                  >
                    {{ beneficiaryDisplayName }}
                  </p>
                </div>
                <UiCopyToggleButton
                  button-size="2.375rem"
                  icon-size="1rem"
                  icon-dark
                  :tooltip-text="t('transactionDetails.fields.beneficiary')"
                  :copy-text="beneficiaryDisplayName"
                  :disabled="beneficiaryDisplayName === '-'"
                  class="transaction-details__copy-btn"
                />
              </div>

              <div class="transaction-details__row">
                <div class="transaction-details__row-main">
                  <p class="transaction-details__label">
                    {{ t('transactionDetails.fields.transactionId') }}
                  </p>
                  <p class="transaction-details__value">{{ transactionIdentifier }}</p>
                </div>
                <UiCopyToggleButton
                  button-size="2.375rem"
                  icon-size="1rem"
                  icon-dark
                  :tooltip-text="t('transactionDetails.fields.transactionId')"
                  :copy-text="transactionIdentifier"
                  :disabled="transactionIdentifier === '-'"
                  class="transaction-details__copy-btn"
                />
              </div>

              <div class="transaction-details__row">
                <div class="transaction-details__row-main">
                  <p class="transaction-details__label">
                    {{ t('transactionDetails.fields.bankNameAddress') }}
                  </p>
                  <p class="transaction-details__value">{{ bankNameAddress }}</p>
                </div>
                <UiCopyToggleButton
                  button-size="2.375rem"
                  icon-size="1rem"
                  icon-dark
                  :tooltip-text="t('transactionDetails.fields.bankNameAddress')"
                  :copy-text="bankNameAddress"
                  :disabled="bankNameAddress === '-'"
                  class="transaction-details__copy-btn"
                />
              </div>

              <div class="transaction-details__row">
                <div class="transaction-details__row-main">
                  <p class="transaction-details__label">
                    {{ t('transactionDetails.fields.reference') }}
                  </p>
                  <p class="transaction-details__value transaction-details__value--multiline">
                    {{ reference }}
                  </p>
                </div>
                <UiCopyToggleButton
                  button-size="2.375rem"
                  icon-size="1rem"
                  icon-dark
                  :tooltip-text="t('transactionDetails.fields.reference')"
                  :copy-text="reference"
                  :disabled="reference === '-'"
                  class="transaction-details__copy-btn"
                />
              </div>

              <div v-if="initiatedByName !== '-'" class="transaction-details__row">
                <div class="transaction-details__row-main">
                  <p class="transaction-details__label">
                    {{ t('transactionDetails.fields.initiatedBy') }}
                  </p>
                  <p class="transaction-details__value">{{ initiatedByName }}</p>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="transaction-details__state">
            <p class="transaction-details__state-message">{{ stateMessage }}</p>
          </div>
        </div>

        <slot name="footer-message" />

        <div class="transaction-details__footer">
          <slot name="footer-action" :dialog-data="dialogData" :close="handleClose" />
          <UiButton
            severity="primary"
            icon="pi pi-download"
            class="transaction-details__footer-btn"
            data-testid="transaction-details-download-statement"
            :label="
              isDownloadingStatement
                ? t('transactionDetails.actions.downloadingStatement')
                : t('transactionDetails.actions.downloadStatement')
            "
            :disabled="!canDownloadStatement || isDownloadingStatement"
            :loading="isDownloadingStatement"
            @click="handleDownloadStatement"
          />
        </div>
      </div>
    </template>
  </UiDialogShell>
</template>

<style lang="scss" scoped>
// Figma note 2895:232699 — the sheet is docked to the right edge with no gap and
// is rounded on its left corners only. `position="right"` handles the docking;
// PrimeVue still insets and caps the height of a positioned dialog.
:global(.p-dialog-mask.transaction-details__mask) {
  align-items: stretch;
  padding: 0;
}

:global(.transaction-details__mask .ui-dialog-shell__surface) {
  height: 100%;
  border-radius: inherit;
}

:global(.transaction-details__mask .p-dialog) {
  margin: 0;
  max-height: 100dvh;
  height: 100dvh;
  border-start-start-radius: #{$radius-24};
  border-end-start-radius: #{$radius-24};
  border-start-end-radius: 0;
  border-end-end-radius: 0;
}

.transaction-details {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;

  // ::after is the last child, so the sections need a layer of their own.
  &__header,
  &__body,
  &__footer {
    position: relative;
    z-index: 1;
  }

  // Figma 3292:199458 splits the footer evenly between the actions.
  &__footer-btn {
    flex: 1 1 0;
    min-width: 0;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $spacing-md;
    padding: $spacing-lg;
  }

  &__title {
    margin: 0;
    color: $color-text-primary;
    font-size: $font-size-2xl;
    font-weight: $font-weight-semibold;
    line-height: $line-height-lg;
    letter-spacing: -0.096px;
  }

  &__body {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 0 $spacing-lg;
  }

  &__details-content {
    display: flex;
    flex-direction: column;
    gap: $spacing-lg;
  }

  &__summary {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $spacing-sm;
    text-align: center;
  }

  &__direction-tag {
    background: $color-button-secondary-idle;
  }

  // The Figma glyph is drawn at 42% of its 20px frame, so the frame is what is
  // sized here — a font icon at 20px would render far larger.
  &__direction-icon {
    width: $icon-size-md;
    height: $icon-size-md;
  }

  &__amount {
    margin: 0;
    width: 100%;
    color: $color-text-primary;
    font-size: $font-size-xl;
    font-weight: $font-weight-semibold;
    line-height: $line-height-lg;
  }

  &__date {
    margin: 0;
    width: 100%;
    color: $color-text-secondary;
    font-size: $font-size-sm;
    font-weight: $font-weight-regular;
    line-height: $line-height-sm;
  }

  &__state {
    display: flex;
    flex: 1;
    min-height: 14rem;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: $spacing-lg;
    border-radius: $radius-16;
    background: $color-bg-canvas;
  }

  &__state--loading {
    flex-direction: column;
    gap: $spacing-md;
  }

  &__state-message {
    margin: 0;
    font-size: $font-size-md;
    font-weight: $font-weight-medium;
    color: $color-text-muted;
  }

  &__spinner {
    width: 2.5rem;
    height: 2.5rem;
  }

  &__rows {
    display: flex;
    flex-direction: column;
  }

  // Dividers are gone; the 8px block padding on neighbouring rows is the 16px
  // gap the note asks for.
  &__row {
    display: flex;
    align-items: center;
    gap: $spacing-sm;
    padding-block: $spacing-s;
    border-radius: $radius-8;
  }

  &__row--inline {
    justify-content: space-between;
  }

  &__row-main {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: $spacing-s;
    min-width: 0;
    flex: 1;
  }

  &__label {
    margin: 0;
    font-size: $font-size-md;
    font-weight: $font-weight-regular;
    line-height: $line-height-md;
    color: $color-text-placeholder;
    letter-spacing: -0.064px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__value {
    margin: 0;
    font-size: $font-size-md;
    font-weight: $font-weight-medium;
    line-height: $line-height-sm;
    color: $color-text-primary;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__value--multiline {
    white-space: normal;
  }

  &__copy-btn {
    flex-shrink: 0;
  }

  &__sent-from {
    display: flex;
    align-items: center;
    gap: $spacing-s;
    min-width: 0;
  }

  &__account-tag {
    flex-shrink: 0;
    background-color: $color-tag-secondary;
    border-radius: $radius-12;
    padding: $spacing-xs $spacing-s;
    font-size: $font-size-xs;
    font-weight: $font-weight-semibold;
    line-height: $line-height-xs;
    letter-spacing: -0.048px;
    color: $color-brand-700;
    white-space: nowrap;
  }

  &__value-inline {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: $font-size-md;
    font-weight: $font-weight-medium;
    color: $color-text-primary;
  }

  &__footer {
    display: flex;
    gap: $spacing-s;
    padding: $spacing-lg;
    border-top: 1px solid $color-table-divider;
  }

  &__footer-btn,
  &__footer :deep(.transaction-details__footer-btn) {
    flex: 1;
    min-width: 0;

    :deep(.p-button-label) {
      white-space: nowrap;
    }
  }
}
</style>
